import { recruitmentRoundLabel } from '../../services/recruitmentLabels'
import DriveRecruitmentPanel from '../../components/DriveRecruitmentPanel'
import { recruitmentAPI, RecruitmentRound, recruitmentError } from '../../services/recruitmentAPI'
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, CalendarIcon, UsersIcon, ClockIcon, PencilIcon, TrashIcon, LinkIcon, ClipboardDocumentIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'
import { questionPoolAPI, questionBankAPI } from '../../services/api'
import { downloadResume } from '../../services/resumeAPI'

interface Drive {
  id: string
  title: string
  description: string
  startDate: string
  endDate: string
  maxCandidates: number
  registeredCandidates: number
  testDuration: number
  status: 'upcoming' | 'active' | 'completed' | 'cancelled'
  instructions: string
  createdAt: string
  collegeType: 'engineering' | 'management'
  tier: 'tier1' | 'tier2'
  role?: 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales' | 'AI/ML-Assignment'
  registrationLink?: string
  linkedAssessments?: any[]
  registeredCandidatesList?: any[]
  assessment_configs?: { [key: string]: { duration_minutes: number; num_questions: number } }
}

interface Candidate {
  registrationId: number
  id: string
  name: string
  email: string
  status: 'registered' | 'completed' | 'in_progress'
  score?: number
  completedAt?: string
  phone?: string
  aadharNumber?: string
  college?: string
  course?: string
  branch?: string
  year?: string
  rollNumber?: string
  collegePassoutMonth?: string
  tenthPercentage?: string
  twelfthPercentage?: string
  collegePercentage?: string
  registrationDate?: string
  resume?: {
    fileName: string
    fileSize: number
    fileType: string
    uploadedAt: string
    fileContent?: string // Base64 encoded file content (legacy)
    storage?: string
    filePath?: string // Server file path for new resume storage
    hasFile?: boolean // Indicates if file exists on server
  }
}

const normalizeAssessmentName = (name: string | undefined | null) =>
  (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const buildNormalizedAssessmentMap = (assessments: Record<string, any> | undefined | null) => {
  const map: Record<string, any> = {}

  if (!assessments) {
    return map
  }

  Object.entries(assessments).forEach(([key, value]) => {
    map[normalizeAssessmentName(key)] = value
  })

  return map
}

const canonicalizeAssessmentName = (name: string | undefined | null) => {
  const mapping: Record<string, string> = {
    aptitudeandreasoning: 'Aptitude & Reasoning',
    aptitudereasoning: 'Aptitude & Reasoning',
    englishcommunication: 'English Communication',
    presalesfundamentals: 'Pre-Sales Fundamentals',
    presalefundamentals: 'Pre-Sales Fundamentals',
    technicalknowledge: 'Technical Knowledge',
    technicalfundamentals: 'Technical Knowledge',
    fundamentals: 'Technical Knowledge',
    cloudfundamentals: 'Cloud Fundamentals',
    aifundamental: 'AI Fundamental',
    codingassessment: 'Coding Assessment',
    casestudy: 'Case Study',
    aimlassignment: 'AI/ML Assignment',
    aiml: 'AI/ML Assignment',
    quantitativeability: 'Quantitative Ability',
    logicalreasoning: 'Logical Reasoning',
    verbalability: 'Verbal Ability'
  }

  const normalized = normalizeAssessmentName(name)
  return mapping[normalized] || (name ?? '')
}

const parseScoreValue = (score: unknown) => {
  if (typeof score === 'number' && !Number.isNaN(score)) {
    return score
  }
  if (typeof score === 'string') {
    const cleaned = score.replace(/[^0-9.+-]/g, '')
    if (cleaned.trim() === '') {
      return null
    }
    const parsed = Number.parseFloat(cleaned)
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }
  return null
}

interface Assessment {
  id: string
  title: string
  description: string
  category: {
    collegeType: 'engineering' | 'management'
    tier: 'tier1' | 'tier2'
  }
  assessmentType: 'coding' | 'aptitude' | 'fundamentals' | 'case-study'
  duration: number
  totalQuestions: number
  totalPoints: number
  problems: any[]
}

const DriveDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [drive, setDrive] = useState<Drive | null>(null)
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [availableAssessments, setAvailableAssessments] = useState<Assessment[]>([])
  const [showAssessmentDetailsModal, setShowAssessmentDetailsModal] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [emailSending, setEmailSending] = useState(false)
  const sendTpoEmail = async () => {
    setEmailSending(true)
    try { await recruitmentAPI.emailTpo(Number(id)); toast.success('Test registration email sent to TPO') }
    catch (error) { toast.error(recruitmentError(error)) } finally { setEmailSending(false) }
  }
  const [recruitmentRounds, setRecruitmentRounds] = useState<RecruitmentRound[]>([])
  useEffect(() => { if (id) recruitmentAPI.configuration(Number(id)).then(({ data }) => setRecruitmentRounds(data.rounds.filter(r => r.kind !== 'assessment'))).catch(() => {}) }, [id])
  
  // Results state
  const [results, setResults] = useState<any[]>([])
  const [resultsLoading, setResultsLoading] = useState(false)
  const [minScoreFilter, setMinScoreFilter] = useState<number | ''>('')
  const [minFinalScoreFilter, setMinFinalScoreFilter] = useState<number | ''>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [assessments, setAssessments] = useState<any[]>([])
  const [isEditingWeights, setIsEditingWeights] = useState(false)
  const [tempWeights, setTempWeights] = useState<Record<string, number>>({})
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('')
  // Overview statistics sourced from drive results API
  const [stats, setStats] = useState<{ completed: number; inProgress: number } | null>(null)

  useEffect(() => {
    if (id) {
      loadDriveDetails(id)
      loadCandidates(id)
      loadResultsSummary(id)
    }
  }, [id])

  // Filter candidates based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCandidates(candidates)
    } else {
      const filtered = candidates.filter(candidate =>
        candidate.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.college?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.course?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.branch?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.rollNumber?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredCandidates(filtered)
    }
  }, [candidates, searchQuery])

  // Set loading to false when drive is found and candidates are loaded
  useEffect(() => {
    if (drive && candidates.length >= 0) {
      setIsLoading(false)
    }
  }, [drive, candidates])

  useEffect(() => {
    if (drive) {
      loadAvailableAssessments()
    }
  }, [drive])
  
  // Load results when results tab is active
  useEffect(() => {
    if (activeTab === 'results' && id) {
      loadResults()
    }
  }, [activeTab, id, minScoreFilter, statusFilter])

  const loadDriveDetails = async (driveId: string) => {
    try {
      // Call the actual API using relative URL for IIS proxy
      const response = await fetch(`/api/v1/drives/${driveId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        if (response.status === 404) {
          setDrive(null)
          setIsLoading(false)
          return
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const apiDrive = await response.json()
      
      // Transform API response to match frontend interface
      const transformedDrive: Drive = {
        id: apiDrive.id.toString(),
        title: apiDrive.title,
        description: apiDrive.description || '',
        startDate: apiDrive.start_date,
        endDate: apiDrive.end_date,
        maxCandidates: apiDrive.max_candidates,
        registeredCandidates: apiDrive.registered_candidates,
        testDuration: apiDrive.total_duration_minutes,
        status: apiDrive.status as 'upcoming' | 'active' | 'completed' | 'cancelled',
        instructions: apiDrive.instructions || '',
        createdAt: apiDrive.created_at,
        collegeType: apiDrive.college_type as 'engineering' | 'management',
        tier: apiDrive.tier as 'tier1' | 'tier2',
        role: apiDrive.role as 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales' | undefined,
        registrationLink: apiDrive.registration_link,
        linkedAssessments: apiDrive.linked_assessments || [],
        registeredCandidatesList: apiDrive.registered_candidates_list || [],
        assessment_configs: apiDrive.assessment_configs || {}
      }
      
      // Update status based on current date (same logic as list page)
      const now = new Date()
      const startDate = new Date(transformedDrive.startDate)
      const endDate = new Date(transformedDrive.endDate)
      
      if (now < startDate) {
        transformedDrive.status = 'upcoming'
      } else if (now >= startDate && now <= endDate) {
        transformedDrive.status = 'active'
      } else if (now > endDate) {
        transformedDrive.status = 'completed'
      }
      
      setDrive(transformedDrive)
    } catch (error) {
      toast.error('Failed to load drive details')
      setDrive(null)
      setIsLoading(false)
    }
  }

  const loadResultsSummary = async (driveId: string) => {
    try {
      // OPTIMIZED: Use the new summary endpoint which is much faster (single query vs processing all candidates)
      const response = await fetch(`/api/v1/drives/${driveId}/results/summary`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) {
        setStats(null)
        return
      }
      const data = await response.json()
      setStats({ completed: data.completed_candidates || 0, inProgress: data.in_progress_candidates || 0 })
    } catch (e) {
      // Non-blocking for overview; ignore errors
      setStats(null)
    }
  }

  const loadCandidates = async (driveId: string) => {
    try {
      console.log('Loading candidates for drive ID:', driveId)
      
      // Call the API to get drive registrations using relative URL for IIS proxy
      const response = await fetch(`/api/v1/drives/${driveId}/registrations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        if (response.status === 404) {
          setCandidates([])
          return
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const apiRegistrations = await response.json()
      console.log('API registrations:', apiRegistrations)
      
      // Transform API response to match frontend interface
      const candidates: Candidate[] = apiRegistrations.map((registration: any) => {
        const regData = registration.registration_data || {}
        return {
          id: registration.candidate_id.toString(),
          registrationId: registration.id,
          name: `${registration.candidate?.first_name || ''} ${registration.candidate?.last_name || ''}`.trim() || 'Unknown',
          email: registration.candidate?.email || '',
          status: registration.status || 'registered',
          score: registration.score,
          completedAt: registration.completed_at,
          phone: regData.contactNumber || regData.phone || '',
          aadharNumber: regData.aadharNumber || '',
          college: regData.college || '',
          course: regData.course || '',
          branch: regData.branch || '',
          year: regData.collegePassoutYear || regData.year || '',
          rollNumber: regData.rollNumber || regData.roll_number || '',
          collegePassoutMonth: regData.collegePassoutMonth || '',
          tenthPercentage: regData.tenthPercentage || '',
          twelfthPercentage: regData.twelfthPercentage || '',
          collegePercentage: regData.collegePercentage || '',
          registrationDate: registration.registered_at,
          resume: regData.resume
        }
      })
      
      setCandidates(candidates)
      console.log('Final loaded candidates:', candidates)
      
    } catch (error) {
      console.error('Error loading candidates:', error)
      toast.error('Failed to load candidates')
      setCandidates([])
    }
  }

  const copyRegistrationLink = () => {
    if (drive?.registrationLink) {
      const registrationUrl = `${window.location.origin}/register/${drive.registrationLink}`
      navigator.clipboard.writeText(registrationUrl).then(() => {
        toast.success('Registration link copied to clipboard!')
      }).catch(() => {
        toast.error('Failed to copy link')
      })
    }
  }

  const handleRemoveCandidate = async (candidateId: string, candidateEmail: string) => {
    if (!drive) return

    console.log('=== REMOVE CANDIDATE DEBUG ===')
    console.log('Drive ID:', drive.id)
    console.log('Candidate ID to remove:', candidateId)
    console.log('Candidate Email to remove:', candidateEmail)

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to remove this candidate from the drive?\n\nCandidate: ${candidateEmail}\n\nThis action cannot be undone.`
    )

    if (!confirmed) return

    try {
      // Call the API to delete the candidate registration using relative URL for IIS proxy
      const response = await fetch(`/api/v1/drives/${drive.id}/registrations/${candidateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Failed to delete candidate. Status: ${response.status}`)
      }

      const result = await response.json()
      console.log('API response:', result)

      // Update local state
      setCandidates(prevCandidates => 
        prevCandidates.filter(candidate => candidate.id !== candidateId)
      )

      // Update drive state
      setDrive(prevDrive => prevDrive ? {
          ...prevDrive,
        registeredCandidates: prevDrive.registeredCandidates - 1
      } : null)

      toast.success('Candidate removed from drive successfully!')
      console.log('=== REMOVE CANDIDATE DEBUG END ===')

    } catch (error) {
      console.error('Error removing candidate:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to remove candidate. Please try again.')
    }
  }

  const loadAvailableAssessments = async () => {
    try {
      if (!drive) return
      
      // Generate dynamic assessments based on drive configuration
      const generateDynamicAssessments = async () => {
        const assessments = []
        
        // Read assessment configurations from the drive's actual configuration
        if (drive.assessment_configs && Object.keys(drive.assessment_configs).length > 0) {
          console.log('Drive assessment configs:', drive.assessment_configs)
          
          // Map assessment type to display name
          const assessmentTypeNames: { [key: string]: string } = {
            'aptitude_reasoning': 'Aptitude & Reasoning',
            'fundamentals': 'Technical Knowledge',
            'cloud_fundamentals': 'Cloud Fundamentals',
            'presales_fundamentals': 'Pre-Sales Fundamentals',
            'english_communication': 'English Communication',
            'ai_fundamental': 'AI Fundamental',
            'coding': 'Coding Assessment',
            'case-study': 'Case Study',
            'quantitative_ability': 'Quantitative Ability',
            'logical_reasoning': 'Logical Reasoning',
            'verbal_ability': 'Verbal Ability'
          }
          
          // Helper to map our assessment types to backend question_type values
          const mapType = (t: string) => {
            if (t === 'case-study') return 'case_study'
            if (t === 'business-simulations') return 'business_simulation'
            return t
          }
          
          // Category key in DB is like 'engineering_tier1' or 'management_tier2'
          const bankCategory = `${drive.collegeType}_${drive.tier}`
          
          let index = 0
          for (const [assessmentType, config] of Object.entries(drive.assessment_configs)) {
            try {
              // 1) List question IDs for this bank category and type
              // Use assessment type as category since questions are stored by assessment type
              let listResp = await questionBankAPI.getQuestions({
                category: assessmentType, // Use assessment type as category (e.g., "aptitude_reasoning")
                question_type: mapType(assessmentType), // Also filter by question type
                is_verified: true,
                limit: 20,
              })
              let items = Array.isArray(listResp.data) ? listResp.data : []
              
              // If no questions found, try without question_type filter (fallback)
              if (items.length === 0) {
                console.log(`No questions found for ${assessmentType}, trying without question_type filter`)
                listResp = await questionBankAPI.getQuestions({
                  category: assessmentType,
                  is_verified: true,
                  limit: 20,
                })
                items = Array.isArray(listResp.data) ? listResp.data : []
              }
              
              // If still no questions, try alternative categories for aptitude_reasoning
              if (items.length === 0 && assessmentType === 'aptitude_reasoning') {
                console.log(`No questions found for aptitude_reasoning, trying separate aptitude and reasoning categories`)
                const aptitudeResp = await questionBankAPI.getQuestions({
                  category: 'aptitude',
                  limit: 10,
                })
                const reasoningResp = await questionBankAPI.getQuestions({
                  category: 'reasoning',
                  limit: 10,
                })
                const aptitudeItems = Array.isArray(aptitudeResp.data) ? aptitudeResp.data : []
                const reasoningItems = Array.isArray(reasoningResp.data) ? reasoningResp.data : []
                items = [...aptitudeItems, ...reasoningItems]
              }
              
              const ids = items.map((q: any) => q.id).slice(0, config.num_questions)
              
              // 2) Fetch full details for each question id
              const details = await Promise.all(ids.map((id: number) => questionBankAPI.getQuestion(id)))
              const questions = details.map((r: any) => r.data)
              
              assessments.push({
                id: `config_${drive.id}_${assessmentType}_${index}`,
                title: assessmentTypeNames[assessmentType] || assessmentType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                description: '',
                category: {
                  collegeType: drive.collegeType,
                  tier: drive.tier,
                  role: drive.role
                },
                assessmentType: assessmentType,
                duration: config.duration_minutes,
                totalQuestions: config.num_questions,
                totalPoints: questions.reduce((sum: number, q: any) => sum + (q.points || 0), 0),
                problems: questions,
                isDynamic: true,
                questionBankCategories: [assessmentType],
                numQuestions: config.num_questions
              } as any)
            } catch (err) {
              console.error('Error fetching questions for', assessmentType, err)
              assessments.push({
                id: `config_${drive.id}_${assessmentType}_${index}`,
                title: assessmentTypeNames[assessmentType] || assessmentType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                description: '',
                category: {
                  collegeType: drive.collegeType,
                  tier: drive.tier,
                  role: drive.role
                },
                assessmentType: assessmentType,
                duration: config.duration_minutes,
                totalQuestions: config.num_questions,
                totalPoints: 0,
                problems: [],
                isDynamic: true,
                questionBankCategories: [assessmentType],
                numQuestions: config.num_questions
              } as any)
            }
            index++
          }
        } else {
          console.log('No assessment_configs found in drive, using fallback')
          // Fallback to hardcoded assessments if no configs are found
          if (drive.collegeType === 'engineering') {
            const fallbackAssessments = [
              { name: 'Aptitude & Reasoning', type: 'aptitude_reasoning', questions: 10, duration: 30 },
              { name: 'Technical Knowledge', type: 'fundamentals', questions: 5, duration: 15 },
              { name: 'Coding Assessment', type: 'coding', questions: 3, duration: 45 }
            ]
            
            for (let i = 0; i < fallbackAssessments.length; i++) {
              const fallback = fallbackAssessments[i]
              assessments.push({
                id: `fallback_${drive.id}_${fallback.type}_${i}`,
                title: fallback.name,
                description: '',
                category: {
                  collegeType: drive.collegeType,
                  tier: drive.tier,
                  role: drive.role
                },
                assessmentType: fallback.type,
                duration: fallback.duration,
                totalQuestions: fallback.questions,
                totalPoints: 0,
                problems: [],
                isDynamic: true,
                questionBankCategories: [fallback.type],
                numQuestions: fallback.questions
              } as any)
            }
          }
        }
        
        return assessments
      }
      
      const dynamicAssessments = await generateDynamicAssessments()
      setAvailableAssessments(dynamicAssessments)
    } catch (error) {
      console.error('Error loading available assessments:', error)
      setAvailableAssessments([])
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800'
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'completed':
        return 'bg-gray-100 text-gray-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getCandidateStatusColor = (status: string) => {
    switch (status) {
      case 'registered':
        return 'bg-blue-100 text-blue-800'
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleEdit = () => {
    if (drive) {
      // Navigate to edit page with drive data
      navigate(`/recruiter/drives/${drive.id}/edit`, { 
        state: { drive } 
      })
    }
  }

  const handleDelete = async () => {
    if (!drive) return
    
    if (window.confirm('Are you sure you want to delete this drive? This action cannot be undone.')) {
      try {
        setIsDeleting(true)
        console.log('Deleting drive via API:', drive.id)
        
        const response = await fetch(`/api/v1/drives/${drive.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error(`Failed to delete drive. Status: ${response.status}`)
        }
        
        // Remove drive from localStorage
        const savedDrives = localStorage.getItem('recruiterDrives')
        if (savedDrives) {
          const drives = JSON.parse(savedDrives)
          const updatedDrives = drives.filter((d: Drive) => d.id !== drive.id)
          localStorage.setItem('recruiterDrives', JSON.stringify(updatedDrives))
        }
        
        // Remove associated candidates data
        localStorage.removeItem(`candidates_${drive.id}`)
        
        toast.success('Drive deleted successfully!')
        navigate('/recruiter/drives')
      } catch (error) {
        console.error('Error deleting drive:', error)
        toast.error('Failed to delete drive. Please try again.')
      } finally {
        setIsDeleting(false)
      }
    }
  }

  const handleViewAssessmentDetails = (assessment: Assessment) => {
    setSelectedAssessment(assessment)
    setShowAssessmentDetailsModal(true)
  }

  const handleDownloadResume = async (candidate: Candidate) => {
    if (!candidate.resume || !candidate.registrationId) {
      toast.error('No resume available for this candidate')
      return
    }
    try {
      await downloadResume(candidate.registrationId, candidate.resume.fileName)
      toast.success('Resume download started')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to download resume')
    }
  }

  const loadResults = async () => {
    if (!id) return
    
    try {
      setResultsLoading(true)
      console.log('Loading results for drive:', id)
      
      const params = new URLSearchParams()
      if (minScoreFilter !== '') {
        params.append('min_score', minScoreFilter.toString())
      }
      if (statusFilter) {
        // Map UI filter to backend values
        let backendStatus = statusFilter
        if (statusFilter === 'registered') {
          backendStatus = 'registered'
        } else if (statusFilter === 'in_progress') {
          backendStatus = 'in_progress'
        }
        params.append('status', backendStatus)
      }
      
      const response = await fetch(`/api/v1/drives/${id}/results?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.detail || errorData.message || errorMessage
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage
        }
        console.error('Results API error:', errorMessage, response.status)
        throw new Error(errorMessage)
      }
      
      const resultsData = await response.json()
      console.log('Results data:', resultsData)
      
      setResults(resultsData.candidates || [])
      
      // OPTIMIZED: Use assessments from results if available to avoid extra API call
      if (resultsData.assessments && Array.isArray(resultsData.assessments) && resultsData.assessments.length > 0) {
        // Normalize assessment names for consistency
        const normalizedAssessments = resultsData.assessments.map((item: any) => ({
          ...item,
          name: canonicalizeAssessmentName(item.name)
        }))
        setAssessments(normalizedAssessments)
      } else {
        // Fallback: Load assessments separately if not in response
        await loadAssessments()
      }
      
    } catch (error) {
      console.error('Error loading results:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to load results'
      toast.error(errorMessage)
      setResults([])
    } finally {
      setResultsLoading(false)
    }
  }
  
  const loadAssessments = async () => {
    if (!id) return
    
    try {
      console.log('Loading assessments for drive:', id)
      const response = await fetch(`/api/v1/drives/${id}/assessments`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.detail || errorData.message || errorMessage
        } catch (e) {
          errorMessage = response.statusText || errorMessage
        }
        console.error('Assessments API error:', errorMessage, response.status)
        throw new Error(errorMessage)
      }
      
      const assessmentsData = await response.json()
      console.log('Assessments data:', assessmentsData)

      let normalizedAssessments: any[] = Array.isArray(assessmentsData) ? assessmentsData : []

      normalizedAssessments = normalizedAssessments.map(item => ({
        ...item,
        name: canonicalizeAssessmentName(item.name)
      }))

      if (normalizedAssessments.length > 0) {
        const totalWeight = normalizedAssessments.reduce((sum, item) => sum + (item.weight || 0), 0)
        if (totalWeight === 0) {
          const defaultMap: Record<string, number> = {}
          if (drive?.role === 'Pre-Sales') {
            defaultMap['Aptitude & Reasoning'] = 50
            defaultMap['Pre-Sales Fundamentals'] = 25
            defaultMap['English Communication'] = 25
          } else if (drive?.role === 'Cloud Support Engineer') {
            defaultMap['Aptitude & Reasoning'] = 50
            defaultMap['Cloud Fundamentals'] = 25
            defaultMap['English Communication'] = 25
          } else if (drive?.role === 'ASE') {
            defaultMap['Aptitude & Reasoning'] = 25
            defaultMap['Technical Knowledge'] = 25
            defaultMap['Coding Assessment'] = 50
          } else {
            const count = normalizedAssessments.length
            const base = Math.floor(100 / count)
            const remainder = 100 - base * count
            normalizedAssessments = normalizedAssessments.map((item, index) => ({
              ...item,
              weight: base + (index < remainder ? 1 : 0)
            }))
          }

          if (Object.keys(defaultMap).length > 0) {
            normalizedAssessments = normalizedAssessments.map(item => ({
              ...item,
              weight: defaultMap[item.name] ?? item.weight ?? 0
            }))
          }
        }
      }

      setAssessments(normalizedAssessments)
      
    } catch (error) {
      console.error('Error loading assessments:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to load assessments'
      console.error('Assessment loading error:', errorMessage)
      setAssessments([])
      // Don't show toast for assessments error as it's non-critical for results display
    }
  }
  
  const handleExportResults = async () => {
    if (!id) return
    
    try {
      const exportData = {
        min_score: minScoreFilter !== '' ? minScoreFilter : null,
        min_final_score: minFinalScoreFilter !== '' ? minFinalScoreFilter : null,
        status: statusFilter === 'registered' ? 'registered' : (statusFilter === 'in_progress' ? 'in_progress' : (statusFilter || null))
      }
      
      const response = await fetch(`/api/v1/drives/${id}/results/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exportData)
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      // Get filename from response headers
      const contentDisposition = response.headers.get('content-disposition')
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1] 
        : `Drive_${drive?.title}_Results.xlsx`
      
      // Download file
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Results exported successfully!')
      
    } catch (error) {
      console.error('Error exporting results:', error)
      toast.error('Failed to export results')
    }
  }
  
  const handleUpdateAssessmentWeight = async (assessmentId: number, weight: number) => {
    if (!id) return
    
    try {
      const response = await fetch(`/api/v1/drives/${id}/assessments/${assessmentId}/weight`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ weight })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
      }
      
      toast.success('Assessment weight updated successfully!')
      
      // Reload results to reflect changes
      await loadResults()
      
    } catch (error) {
      console.error('Error updating assessment weight:', error)
      toast.error((error as Error).message || 'Failed to update assessment weight')
    }
  }

  const handleUpdateAllWeights = async (weights: Record<string, number>) => {
    console.log('handleUpdateAllWeights called with:', weights)
    if (!id) {
      console.log('No drive ID found')
      return
    }
    
    try {
      console.log('Making API request to update weights')
      const response = await fetch(`/api/v1/drives/${id}/assessments/weights`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ weights })
      })
      
      console.log('API response status:', response.status)
      
      if (!response.ok) {
        const errorData = await response.json()
        console.log('API error:', errorData)
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
      }
      
      console.log('Showing success toast')
      toast.success('All assessment weights updated successfully!')
      
      // Reload results to reflect changes
      console.log('Reloading results')
      await loadResults()
      
    } catch (error) {
      console.error('Error updating assessment weights:', error)
      toast.error((error as Error).message || 'Failed to update assessment weights')
    }
  }

  const handleEditWeights = () => {
    console.log('Edit weights clicked, current assessments:', assessments)
    // Initialize temp weights with current values
    const currentWeights: Record<string, number> = {}
    assessments.forEach(assessment => {
      currentWeights[assessment.name] = assessment.weight || 0
    })
    console.log('Setting temp weights:', currentWeights)
    setTempWeights(currentWeights)
    setIsEditingWeights(true)
  }

  const handleWeightChange = (assessmentName: string, weight: number) => {
    setTempWeights(prev => ({
      ...prev,
      [assessmentName]: weight
    }))
  }

  const handleSaveWeights = async () => {
    console.log('Save weights clicked, tempWeights:', tempWeights)
    const totalWeight = Object.values(tempWeights).reduce((sum, weight) => sum + weight, 0)
    console.log('Total weight:', totalWeight)
    
    if (totalWeight !== 100) {
      console.log('Weight validation failed, showing error toast')
      toast.error(`All assessment weights must sum to 100%. Current total: ${totalWeight}%`)
      return
    }
    
    console.log('Calling handleUpdateAllWeights')
    const canonicalWeights = Object.entries(tempWeights).reduce<Record<string, number>>((acc, [name, weight]) => {
      const canonicalName = canonicalizeAssessmentName(name)
      if (canonicalName) {
        acc[canonicalName] = weight
      }
      return acc
    }, {})

    await handleUpdateAllWeights(canonicalWeights)
    setIsEditingWeights(false)
    setTempWeights({})
  }

  const handleCancelEditWeights = () => {
    setIsEditingWeights(false)
    setTempWeights({})
  }

  // Filter results based on search query
  const filteredResults = results.filter(candidate => {
    if (statusFilter && candidate.status !== statusFilter) {
      return false
    }

    if (minScoreFilter !== '' && (candidate.custom_score ?? 0) < minScoreFilter) {
      return false
    }

    if (minFinalScoreFilter !== '' && (candidate.final_score ?? 0) < minFinalScoreFilter) {
      return false
    }

    if (!candidateSearchQuery) return true
    const searchLower = candidateSearchQuery.toLowerCase()
    return (
      candidate.name?.toLowerCase().includes(searchLower) ||
      candidate.email?.toLowerCase().includes(searchLower)
    )
  })

  const handleExportCandidates = () => {
    try {
      if (filteredCandidates.length === 0) {
        toast.error('No candidates to export')
        return
      }

      // Prepare data for Excel export
      const exportData = filteredCandidates.map((candidate, index) => ({
        'S.No': index + 1,
        'Name': candidate.name,
        'Email': candidate.email,
        'Phone': candidate.phone || 'N/A',
        'Aadhar Number': candidate.aadharNumber || 'N/A',
        'College': candidate.college || 'N/A',
        'Course': candidate.course || 'N/A',
        'Branch': candidate.branch || 'N/A',
        'Passout Year': candidate.year || 'N/A',
        'Passout Month': candidate.collegePassoutMonth || 'N/A',
        'Roll Number': candidate.rollNumber || 'N/A',
        '10th Percentage': candidate.tenthPercentage || 'N/A',
        '12th Percentage': candidate.twelfthPercentage || 'N/A',
        'College/University Percentage': candidate.collegePercentage || 'N/A',
        'Status': candidate.status.replace('_', ' ').charAt(0).toUpperCase() + candidate.status.slice(1).replace('_', ' '),
        'Registration Date': candidate.registrationDate ? new Date(candidate.registrationDate).toLocaleDateString() : 'N/A',
        'Resume': candidate.resume ? 'Yes' : 'No'
      }))

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Candidates')

      // Auto-size columns
      const maxWidth = 50
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.min(
          maxWidth,
          Math.max(
            key.length,
            ...exportData.map(row => String(row[key as keyof typeof row] || '').length)
          )
        )
      }))
      ws['!cols'] = colWidths

      // Generate filename with drive title and date
      const fileName = `${drive?.title.replace(/[^a-z0-9]/gi, '_')}_Candidates_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`

      // Download file
      XLSX.writeFile(wb, fileName)
      
      toast.success(`Exported ${filteredCandidates.length} candidate(s) successfully!`)
    } catch (error) {
      console.error('Error exporting candidates:', error)
      toast.error('Failed to export candidates')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading drive details...</p>
        </div>
      </div>
    )
  }

  if (!drive) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Drive not found</h2>
          <p className="text-gray-600 mb-6">The drive you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/recruiter/drives')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Drives
          </button>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <h1 className="text-3xl font-bold text-gray-900 mr-4">{drive.title}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(drive.status)}`}>
                  {drive.status}
                </span>
              </div>
              <p className="text-gray-600 mb-3">{drive.description}</p>
              
              <div className="flex items-center space-x-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {drive.collegeType === 'engineering' ? 'Engineering Colleges' : 'Management Colleges'}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {drive.tier === 'tier1' ? 'Tier 1' : 'Tier 2'}
                </span>
                {drive.collegeType === 'engineering' && drive.role && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    {drive.role}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleEdit}
                className="btn btn-outline flex items-center"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                className={`btn btn-outline text-red-600 border-red-300 hover:bg-red-50 flex items-center ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isDeleting}
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'candidates', label: `Candidates (${candidates.length})` },
                { key: 'results', label: 'Results' },
                ...recruitmentRounds.map(r => ({ key: `round-${r.round_id}`, label: recruitmentRoundLabel(r.name) })),
                ...(drive.collegeType === 'management' ? [] : [{ key: 'configure-rounds', label: 'Configure Rounds' }])
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        {(activeTab === 'configure-rounds' && drive.collegeType !== 'management') || activeTab.startsWith('round-') ? (
          <DriveRecruitmentPanel key={activeTab} driveId={Number(id)} roundId={activeTab.startsWith('round-') ? Number(activeTab.slice(6)) : undefined} onRoundsChange={setRecruitmentRounds} />
        ) : activeTab === 'overview' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Drive Information */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Drive Information</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Start Date</p>
                        <p className="font-medium">{formatDate(drive.startDate)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">End Date</p>
                        <p className="font-medium">{formatDate(drive.endDate)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <UsersIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Candidates</p>
                        <p className="font-medium">{drive.registeredCandidates} / {drive.maxCandidates}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm text-gray-600">Available Assessments</p>
                        <p className="font-medium">{availableAssessments.length} assessments</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <ClockIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Test Duration</p>
                        <p className="font-medium">{drive.testDuration} minutes</p>
                      </div>
                    </div>
                  </div>

                  {/* Registration Link Section */}
                  {drive.registrationLink && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-blue-900 flex items-center">
                          <LinkIcon className="w-5 h-5 mr-2" />
                          Registration Link
                        </h4>
                        <button
                          onClick={copyRegistrationLink}
                          className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                        >
                          <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                          Copy Link
                        </button>
                      </div>
                      <div className="bg-white p-3 rounded border border-blue-200">
                        <p className="text-sm text-gray-600 mb-2">Send this Round 1 link to the TPO to forward to students:</p>
                        <p className="font-mono text-sm text-blue-800 break-all">
                          {window.location.origin}/register/{drive.registrationLink}
                        </p>
                        <button disabled={emailSending} onClick={() => void sendTpoEmail()} className="mt-3 rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50">{emailSending ? 'Sending...' : 'Send Test Email to TPO'}</button>
                      </div>
                    </div>
                  )}
                  
                  {drive.instructions && (
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Test Instructions</h4>
                      <p className="text-gray-600 whitespace-pre-wrap">{drive.instructions}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Available Assessments Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Assessments</h3>
                
                {availableAssessments.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-600 mb-4">No assessments available for this drive category.</p>
                    <p className="text-sm text-gray-500">
                      Create assessments for {drive.collegeType === 'engineering' ? 'Engineering' : 'Management'} - {drive.tier === 'tier1' ? 'Tier 1' : 'Tier 2'} to see them here.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableAssessments.map((assessment) => (
                      <div key={assessment.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{assessment.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{assessment.description}</p>
                            <div className="flex items-center space-x-4 mt-2">
                              <span className="text-xs text-gray-500 capitalize">{assessment.assessmentType?.replace('_', ' ')}</span>
                              <span className="text-xs text-gray-500">{assessment.totalQuestions || assessment.problems?.length || 0} problems</span>
                              <span className="text-xs text-gray-500">{assessment.duration} min</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Statistics */}
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
                
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{drive.registeredCandidates}</div>
                    <div className="text-sm text-gray-600">Total Registrations</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {stats ? stats.completed : candidates.filter(c => c.status === 'completed').length}
                    </div>
                    <div className="text-sm text-gray-600">Completed Tests</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-600">
                      {stats ? stats.inProgress : candidates.filter(c => c.status === 'in_progress').length}
                    </div>
                    <div className="text-sm text-gray-600">In Progress</div>
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'results' ? (
          /* Results Tab */
          <div className="space-y-6">
            
            {/* Assessment Weights */}
            {assessments.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Assessment Weights</h4>
                  {!isEditingWeights && (
                    <button
                      onClick={handleEditWeights}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Edit Weights
                    </button>
                  )}
                </div>
                
                {/* Total Weight Display */}
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Total Weight:</span>
                    <span className={`text-lg font-bold ${
                      assessments.reduce((sum, a) => sum + (a.weight || 0), 0) === 100 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {assessments.reduce((sum, a) => sum + (a.weight || 0), 0)}%
                    </span>
                  </div>
                  {assessments.reduce((sum, a) => sum + (a.weight || 0), 0) !== 100 && (
                    <p className="text-sm text-red-600 mt-1">
                      ⚠️ Weights must sum to exactly 100%
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {assessments.map((assessment) => {
                    console.log('Rendering assessment:', assessment.name, 'isEditingWeights:', isEditingWeights)
                    return (
                    <div key={assessment.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-900">{assessment.name}</h5>
                        <span className="text-sm text-gray-500">{assessment.assessment_type}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-600">Weight:</label>
                        {isEditingWeights ? (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={tempWeights[assessment.name] || assessment.weight || 0}
                            onChange={(e) => handleWeightChange(assessment.name, Number(e.target.value))}
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        ) : (
                          <span className="w-16 px-2 py-1 bg-gray-100 rounded text-sm text-gray-700 inline-block text-center">
                            {assessment.weight || 0}
                          </span>
                        )}
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                    </div>
                    )
                  })}
                </div>

                {/* Edit Controls */}
                {isEditingWeights && (
                  <div className="mt-6 flex items-center justify-end space-x-3">
                    <button
                      onClick={handleCancelEditWeights}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveWeights}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      Save Weights
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Results Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                {/* Search and Filters */}
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search candidates..."
                        value={candidateSearchQuery}
                        onChange={(e) => setCandidateSearchQuery(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-64"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                <button
                  onClick={handleExportResults}
                  className="btn btn-primary flex items-center"
                  disabled={resultsLoading}
                >
                  <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                  Export to Excel
                </button>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Shortlist(Custom):</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={minScoreFilter}
                    onChange={(e) => setMinScoreFilter(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-20 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="0"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Shortlist(Final):</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={minFinalScoreFilter}
                    onChange={(e) => setMinFinalScoreFilter(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-20 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="0"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Status:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">All</option>
                    <option value="registered">Registered</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>
            
                {resultsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading results...</p>
                  </div>
                ) : filteredResults.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-600">No results found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Candidate
                          </th>
                          {assessments.map((assessment) => {
                            // For AI/ML Assignment, create two columns
                            if (assessment.name === 'AI/ML Assignment') {
                              return (
                                <React.Fragment key={assessment.id}>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    AI/ML Assignment (URL)
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    AI/ML Assignment Marks
                                  </th>
                                </React.Fragment>
                              )
                            }
                            // For other assessments, show single column
                            return (
                              <th key={assessment.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {assessment.name}
                              </th>
                            )
                          })}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Custom Score
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Final Score
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Time (mins)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredResults.map((candidate) => {
                          const candidateAssessments = candidate.assessments || {}
                          const normalizedAssessmentMap = buildNormalizedAssessmentMap(candidateAssessments)

                          const findAssessmentData = (assessmentName: string) => {
                            if (!assessmentName) {
                              return undefined
                            }
                            return (
                              candidateAssessments?.[assessmentName] ??
                              normalizedAssessmentMap[normalizeAssessmentName(assessmentName)]
                            )
                          }

                          return (
                            <tr key={candidate.candidate_id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{candidate.name || candidate.email}</div>
                                  {candidate.name && candidate.email && (
                                    <div className="text-sm text-gray-500">{candidate.email}</div>
                                  )}
                                </div>
                              </td>
                              {assessments.map((assessment) => {
                                const assessmentData = findAssessmentData(assessment.name)
                                const rawScore =
                                  typeof assessmentData?.raw_score === 'string'
                                    ? assessmentData.raw_score.trim()
                                    : ''
                                const parsedScore = assessmentData ? parseScoreValue(assessmentData.score) : null
                                const hasRawScore = rawScore !== '' && rawScore !== '-' && rawScore !== '0/0'
                                const hasData = !!assessmentData && (parsedScore !== null || hasRawScore)
                                const scoreToDisplay = parsedScore ?? (hasRawScore ? 0 : null)
                                
                                // Special handling for AI/ML Assignment
                                if (assessment.name === 'AI/ML Assignment') {
                                  const assignmentLink = assessmentData?.assignment_link || '-'
                                  const marks = assessmentData?.score ?? null
                                  
                                  return (
                                    <React.Fragment key={assessment.id}>
                                      {/* Assignment URL Column */}
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm">
                                          {assignmentLink && assignmentLink !== '-' ? (
                                            <a
                                              href={assignmentLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline max-w-xs truncate block"
                                              title={assignmentLink}
                                            >
                                              {assignmentLink.length > 40 ? `${assignmentLink.substring(0, 40)}...` : assignmentLink}
                                            </a>
                                          ) : (
                                            <span className="text-gray-400">Not submitted</span>
                                          )}
                                        </div>
                                      </td>
                                      {/* Assignment Marks Column */}
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm">
                                          {marks !== null && marks !== undefined ? (
                                            <div>
                                              <div className="font-medium text-gray-900">
                                                {marks}/100
                                              </div>
                                              <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                className="mt-1 w-20 px-2 py-1 text-xs border border-gray-300 rounded"
                                                placeholder="Enter marks"
                                                defaultValue={marks}
                                                onBlur={async (e) => {
                                                  const newMarks = parseFloat(e.target.value)
                                                  if (!isNaN(newMarks) && newMarks >= 0 && newMarks <= 100) {
                                                    try {
                                                      const token = localStorage.getItem('access_token')
                                                      // Use 'aiml' instead of 'aiml_assignment' to match database format
                                                      const assessmentId = `${id}_aiml`
                                                      const response = await fetch(`/api/v1/drives/${id}/assignments/${candidate.candidate_id}/marks`, {
                                                        method: 'PUT',
                                                        headers: {
                                                          'Content-Type': 'application/json',
                                                          'Authorization': `Bearer ${token}`
                                                        },
                                                        body: JSON.stringify({ 
                                                          assessment_id: assessmentId,
                                                          marks: newMarks 
                                                        })
                                                      })
                                                      if (response.ok) {
                                                        toast.success('Marks updated successfully')
                                                        loadResults() // Reload results
                                                      } else {
                                                        const errorData = await response.json()
                                                        toast.error(errorData.detail || 'Failed to update marks')
                                                      }
                                                    } catch (error) {
                                                      console.error('Error updating marks:', error)
                                                      toast.error('Failed to update marks')
                                                    }
                                                  }
                                                }}
                                              />
                                            </div>
                                          ) : (
                                            <div>
                                              <span className="text-gray-400 block mb-1">Not marked</span>
                                              <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                className="w-20 px-2 py-1 text-xs border border-gray-300 rounded"
                                                placeholder="Enter marks"
                                                onBlur={async (e) => {
                                                  const newMarks = parseFloat(e.target.value)
                                                  if (!isNaN(newMarks) && newMarks >= 0 && newMarks <= 100) {
                                                    try {
                                                      const token = localStorage.getItem('access_token')
                                                      // Use 'aiml' instead of 'aiml_assignment' to match database format
                                                      const assessmentId = `${id}_aiml`
                                                      const response = await fetch(`/api/v1/drives/${id}/assignments/${candidate.candidate_id}/marks`, {
                                                        method: 'PUT',
                                                        headers: {
                                                          'Content-Type': 'application/json',
                                                          'Authorization': `Bearer ${token}`
                                                        },
                                                        body: JSON.stringify({ 
                                                          assessment_id: assessmentId,
                                                          marks: newMarks 
                                                        })
                                                      })
                                                      if (response.ok) {
                                                        toast.success('Marks updated successfully')
                                                        loadResults() // Reload results
                                                      } else {
                                                        const errorData = await response.json()
                                                        toast.error(errorData.detail || 'Failed to update marks')
                                                      }
                                                    } catch (error) {
                                                      console.error('Error updating marks:', error)
                                                      toast.error('Failed to update marks')
                                                    }
                                                  }
                                                }}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </React.Fragment>
                                  )
                                }
                                
                                // Normal assessment rendering
                                return (
                                  <td key={assessment.id} className="px-6 py-4 whitespace-nowrap">
                                    {hasData ? (
                                      <div className="text-sm">
                                        <div className="font-medium text-gray-900">
                                          {(rawScore || '-')} {scoreToDisplay !== null ? `(${scoreToDisplay.toFixed(1)}%)` : ''}
                                        </div>
                                        <div className="text-gray-500">
                                          {assessmentData?.time_taken_minutes ?? 0} min
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 text-sm">-</span>
                                    )}
                                  </td>
                                )
                              })}
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {candidate.custom_score !== undefined && candidate.custom_score !== null ? 
                                  candidate.custom_score.toFixed(2) + '%' : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-900">
                                {candidate.final_score !== undefined && candidate.final_score !== null ? 
                                  candidate.final_score.toFixed(2) + '%' : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {candidate.total_time_minutes !== undefined && candidate.total_time_minutes !== null ? 
                                  candidate.total_time_minutes : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  candidate.status === 'completed' 
                                    ? 'bg-green-100 text-green-800'
                                    : candidate.status === 'in_progress'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {candidate.status.replace('_', ' ')}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Candidates Tab */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Registered Candidates</h3>
                {candidates.length > 0 && (
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search candidates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                    <button
                      onClick={handleExportCandidates}
                      className="btn btn-primary flex items-center"
                      title="Export candidates to Excel"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      Export
                    </button>
                  </div>
                )}
              </div>
              
              {candidates.length === 0 ? (
                <div className="text-center py-8">
                  <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No candidates registered yet.</p>
                </div>
              ) : filteredCandidates.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-gray-600">No candidates found matching "{searchQuery}"</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Candidate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Resume
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCandidates.map((candidate) => (
                        <tr key={candidate.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                              <div className="text-sm text-gray-500">{candidate.email}</div>
                              {candidate.phone && (
                                <div className="text-xs text-gray-400">{candidate.phone}</div>
                              )}
                              {candidate.college && (
                                <div className="text-xs text-gray-400">{candidate.college}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCandidateStatusColor(candidate.status)}`}>
                              {candidate.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {candidate.resume ? (
                              <div className="flex items-center space-x-2">
                                <div className="flex items-center">
                                  <svg className="w-4 h-4 text-green-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <div className="text-xs">
                                    <div className="font-medium text-gray-900">{candidate.resume.fileName}</div>
                                    <div className="text-gray-500">
                                      {(candidate.resume.fileSize / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDownloadResume(candidate)}
                                  className={`flex items-center px-2 py-1 text-xs rounded transition-colors ${
                                    candidate.resume.hasFile 
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  }`}
                                  title={candidate.resume.hasFile ? "Download actual resume file" : "Download resume information (original file not available)"}
                                >
                                  <ArrowDownTrayIcon className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">No resume</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleRemoveCandidate(candidate.id, candidate.email)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="Remove candidate from drive"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Assessment Details Modal */}
      {showAssessmentDetailsModal && selectedAssessment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Assessment Details
              </h3>
              <button
                onClick={() => setShowAssessmentDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Assessment Header */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-semibold text-gray-900">{selectedAssessment.title}</h4>
                <span className="text-sm text-gray-600 capitalize">{selectedAssessment.assessmentType}</span>
              </div>
              <p className="text-gray-600 mb-2">{selectedAssessment.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>Duration: {selectedAssessment.duration} minutes</span>
                <span>Problems: {selectedAssessment.problems?.length || 0}</span>
                <span>Category: {selectedAssessment.category?.collegeType} - {selectedAssessment.category?.tier}</span>
              </div>
            </div>

            {/* Problems List */}
            <div className="space-y-4">
              <h5 className="text-lg font-medium text-gray-900">Problems</h5>
              {selectedAssessment.problems && selectedAssessment.problems.length > 0 ? (
                selectedAssessment.problems.map((problem: any, index: number) => (
                  <div key={problem.id || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h6 className="font-medium text-gray-900">Problem {index + 1}</h6>
                        {problem.title && (
                          <p className="text-sm text-gray-600">{problem.title}</p>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">{problem.points || 0} points</span>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Problem Statement</label>
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{problem.description}</p>
                      </div>

                      {selectedAssessment.assessmentType === 'coding' && (
                        <>
                          {problem.testCases && problem.testCases.length > 0 && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Test Cases</label>
                              <div className="space-y-2">
                                {problem.testCases.map((testCase: any, tcIndex: number) => (
                                  <div key={tcIndex} className="bg-gray-50 p-3 rounded">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>
                                        <span className="text-xs font-medium text-gray-500">Input:</span>
                                        <p className="text-sm text-gray-600 font-mono">{testCase.input}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-medium text-gray-500">Expected Output:</span>
                                        <p className="text-sm text-gray-600 font-mono">{testCase.expectedOutput}</p>
                                      </div>
                                    </div>
                                    {testCase.isHidden && (
                                      <span className="inline-block mt-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                        Hidden Test Case
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {(selectedAssessment.assessmentType === 'aptitude' || selectedAssessment.assessmentType === 'fundamentals') && (
                        <>
                          {problem.options && problem.options.length > 0 && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                              <div className="space-y-2">
                                {problem.options.map((option: any, optIndex: number) => (
                                  <div key={optIndex} className={`p-2 rounded ${option.isCorrect ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                                    <span className="text-sm font-medium text-gray-600">{String.fromCharCode(65 + optIndex)}. </span>
                                    <span className="text-sm text-gray-600">{option.text}</span>
                                    {option.isCorrect && (
                                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                        Correct
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {selectedAssessment.assessmentType === 'case-study' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Details</label>
                            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                              Case study problems are described in the problem statement above.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No problems added to this assessment yet</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAssessmentDetailsModal(false)}
                className="btn btn-outline"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DriveDetailPage
